/**
 * Shared coercion + colour-resolution helpers for storefront sections that read raw setting values out of
 * `section.settings` (a `Record<string, unknown>`). Factored out of featured-collection so every section
 * resolves the same value shapes identically (no drift).
 */
import { isThemeColorToken } from '../../theme/colorTokens.js';
/** Coerce a stored setting value to a number, with a fallback. */
export const num = (v, d) => (typeof v === 'number' ? v : d);
/** Coerce a stored setting value to a string, with a fallback. */
export const str = (v, d) => (typeof v === 'string' ? v : d);
/**
 * Minimal HTML escape for injecting plain text (e.g. a collection's own description) into markup.
 *
 * The type guard is not paranoia. This runs during SSR in 60+ call sites, all reading an untrusted
 * `Record<string, unknown>` document; the TYPE says `string` but a document written by an older builder
 * version — or one field a migration missed — hands it `undefined`, and `undefined.replace` throws. Under
 * React 18 streaming SSR an uncontained throw does not render "one broken section", it emits an EMPTY page
 * with HTTP 200 (see SectionErrorBoundary). One line here removes that entire class of blank-page.
 */
export function escapeHtml(s) {
    if (typeof s !== 'string')
        return '';
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/**
 * Resolve a stored colour value ({ref:'custom',value}|{ref:'theme',token}) to a CSS colour string.
 * The token branch is ALLOWLISTED to the canonical `THEME_COLOR_TOKENS` (0019 finding #18): an
 * unknown/legacy/hostile token string falls through to `fallback` rather than interpolating an arbitrary
 * string into `var(--aqx-theme-<x>)`. This is the front's second validation layer (alongside the
 * `applyThemeVars` sanitizer). Saved `brand`/`line`/`background`/`lavender` tokens are remapped to the
 * new names by `hydrateDocument` before render, so they resolve after remap.
 */
export function resolveColor(v, fallback) {
    if (v && typeof v === 'object') {
        const o = v;
        if (typeof o.value === 'string')
            return o.value;
        if (typeof o.token === 'string' && isThemeColorToken(o.token))
            return `var(--aqx-theme-${o.token})`;
    }
    return fallback;
}
/**
 * A colour value that is SAFE TO INTERPOLATE INTO CSS.
 *
 * `resolveColor` allowlists the theme-token branch but passes the `{ref:'custom',value}` branch straight
 * through, and a stored document is JSON: whatever wrote it, the value that comes back can be any string.
 * Sections drop these into a scoped `<style>`, where `#f00} .card{display:none` closes the rule and opens
 * one of its own — a stored document that rewrites the page around it.
 *
 * Accepted: a hex colour, a `var(--aqx-…)` reference, an rgb/rgba/hsl/hsla call with nothing but numbers
 * and separators inside, and the CSS-wide keywords. Everything else falls back, because a colour nobody
 * can name is a colour nobody needs.
 */
export function cssColor(value, fallback) {
    const resolved = resolveColor(value, fallback).trim();
    return SAFE_COLOR.test(resolved) ? resolved : fallback;
}
const SAFE_COLOR = /^(#[0-9a-f]{3,8}|var\(--[a-z0-9-]+\)|(rgb|rgba|hsl|hsla)\([0-9.,%\s/deg]+\)|[a-z]{3,20})$/i;
//# sourceMappingURL=settingValue.js.map